import React from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
interface SessionSetupProps {
  exercises: any[];
  updateExercise: (index: number, data: any) => void;
  onBack: () => void;
  onNext: () => void;
}
export const SessionSetup = ({
  exercises,
  updateExercise,
  onBack,
  onNext
}: SessionSetupProps) => {
  // Check if an exercise needs weight input
  const needsWeight = exercise => {
    const noWeightExercises = ['plank', 'treadmill', 'bike', 'elliptical', 'rower'];
    return !noWeightExercises.includes(exercise.id);
  };
  // Check if an exercise is cardio
  const isCardio = exercise => {
    return exercise.isCardio;
  };
  // Check if exercise supports equipment selection
  const hasEquipmentOptions = exercise => {
    return exercise.equipmentOptions && exercise.equipmentOptions.length > 0;
  };
  // Check if exercise supports barbell
  const supportsBarbell = exercise => {
    return exercise.equipmentOptions && exercise.equipmentOptions.includes('barbell');
  };
  // Calculate weight per side (total weight minus bar weight, divided by 2)
  const calculateWeightPerSide = (totalWeight, barWeight) => {
    const weightPerSide = (totalWeight - barWeight) / 2;
    return weightPerSide > 0 ? weightPerSide : 0;
  };
  return <div className="flex flex-col h-full" data-id="element-108">
      <div className="p-6" data-id="element-109">
        <button onClick={onBack} className="text-gray-500 mb-4" data-id="element-110">
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-111">Session Setup</h1>
        <p className="text-gray-600 mb-4" data-id="element-112">
          Define your sets, reps, and starting weights
        </p>
      </div>
      <div className="flex-1 px-6 pb-6 overflow-y-auto" data-id="element-113">
        <div className="space-y-4" data-id="element-114">
          {exercises.map((exercise, index) => <Card key={index} className="p-4" data-id="element-115">
              <h3 className="font-medium mb-3" data-id="element-116">{exercise.name}</h3>
              {exercise.lastSession && <div className="mb-3 bg-blue-50 p-2 rounded-md" data-id="element-117">
                  <p className="text-sm text-blue-700" data-id="element-118">
                    Last session: {exercise.lastSession.weight}lb on{' '}
                    {new Date(exercise.lastSession.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
                  </p>
                </div>}
              {hasEquipmentOptions(exercise) && <div className="mb-4" data-id="element-119">
                  <label className="block text-sm text-gray-500 mb-2" data-id="element-120">
                    Equipment
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-121">
                    {exercise.equipmentOptions.map(equipment => <button key={equipment} onClick={() => updateExercise(index, {
                equipment: equipment
              })} className={`px-3 py-1 rounded-full text-sm ${exercise.equipment === equipment ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-122">
                        {equipment.charAt(0).toUpperCase() + equipment.slice(1)}
                      </button>)}
                  </div>
                </div>}
              {supportsBarbell(exercise) && exercise.equipment === 'barbell' && <div className="mb-4" data-id="element-123">
                    <label className="block text-sm text-gray-500 mb-2" data-id="element-124">
                      Bar Weight
                    </label>
                    <div className="flex gap-2" data-id="element-125">
                      <button onClick={() => updateExercise(index, {
                barWeight: 45
              })} className={`px-3 py-1 rounded-full text-sm ${exercise.barWeight === 45 ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-126">
                        45lb Bar
                      </button>
                      <button onClick={() => updateExercise(index, {
                barWeight: 35
              })} className={`px-3 py-1 rounded-full text-sm ${exercise.barWeight === 35 ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-127">
                        35lb Bar
                      </button>
                    </div>
                  </div>}
              {isCardio(exercise) && <div className="mb-4" data-id="element-128">
                  <label className="block text-sm text-gray-500 mb-2" data-id="element-129">
                    Tracking Type
                  </label>
                  <div className="flex rounded-lg overflow-hidden border border-gray-200" data-id="element-130">
                    <button onClick={() => updateExercise(index, {
                trackingType: 'calories'
              })} className={`flex-1 py-2 text-center ${exercise.trackingType === 'calories' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`} data-id="element-131">
                      Calories
                    </button>
                    <button onClick={() => updateExercise(index, {
                trackingType: 'time'
              })} className={`flex-1 py-2 text-center ${exercise.trackingType === 'time' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`} data-id="element-132">
                      Time
                    </button>
                  </div>
                </div>}
              <div className={needsWeight(exercise) && !isCardio(exercise) ? 'grid grid-cols-3 gap-4' : 'grid grid-cols-2 gap-4'} data-id="element-133">
                {!isCardio(exercise) && <>
                    <div data-id="element-134">
                      <label className="block text-sm text-gray-500 mb-1" data-id="element-135">
                        Sets
                      </label>
                      <select value={exercise.sets || 3} onChange={e => updateExercise(index, {
                  sets: parseInt(e.target.value)
                })} className="w-full p-2 border border-gray-300 rounded-lg" data-id="element-136">
                        {[1, 2, 3, 4, 5].map(num => <option key={num} value={num} data-id="element-137">
                            {num}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-138">
                      <label className="block text-sm text-gray-500 mb-1" data-id="element-139">
                        Reps
                      </label>
                      <select value={exercise.reps || 10} onChange={e => updateExercise(index, {
                  reps: parseInt(e.target.value)
                })} className="w-full p-2 border border-gray-300 rounded-lg" data-id="element-140">
                        {[5, 8, 10, 12, 15].map(num => <option key={num} value={num} data-id="element-141">
                            {num}
                          </option>)}
                      </select>
                    </div>
                  </>}
                {isCardio(exercise) && <div className="col-span-2" data-id="element-142">
                    <label className="block text-sm text-gray-500 mb-1" data-id="element-143">
                      {exercise.trackingType === 'calories' ? 'Target Calories' : 'Duration (minutes)'}
                    </label>
                    <div className="relative" data-id="element-144">
                      <input type="number" value={exercise.target || (exercise.trackingType === 'calories' ? 200 : 20)} onChange={e => updateExercise(index, {
                  target: parseInt(e.target.value) || 0
                })} className="w-full p-2 border border-gray-300 rounded-lg pr-10" min="1" step={exercise.trackingType === 'time' ? 5 : 50} data-id="element-145" />
                      <span className="absolute right-3 top-2 text-gray-500" data-id="element-146">
                        {exercise.trackingType === 'calories' ? 'cal' : 'min'}
                      </span>
                    </div>
                  </div>}
                {needsWeight(exercise) && !isCardio(exercise) && <div data-id="element-147">
                    <label className="block text-sm text-gray-500 mb-1" data-id="element-148">
                      Weight
                    </label>
                    <div className="relative" data-id="element-149">
                      <input type="number" value={exercise.weight || (exercise.lastSession ? exercise.lastSession.weight : 0)} step="5" onChange={e => updateExercise(index, {
                  weight: parseInt(e.target.value) || 0
                })} className="w-full p-2 border border-gray-300 rounded-lg pr-8" data-id="element-150" />
                      <span className="absolute right-3 top-2 text-gray-500" data-id="element-151">
                        lb
                      </span>
                    </div>
                  </div>}
              </div>
              {/* Weight per side calculation for barbell exercises */}
              {supportsBarbell(exercise) && exercise.equipment === 'barbell' && exercise.weight > 0 && exercise.barWeight && <div className="mt-4 bg-blue-50 p-3 rounded-lg" data-id="element-152">
                    <div className="flex justify-between items-center" data-id="element-153">
                      <span className="text-sm text-blue-700" data-id="element-154">
                        Weight breakdown:
                      </span>
                      <span className="font-medium text-blue-700" data-id="element-155">
                        {exercise.barWeight}lb bar +{' '}
                        {calculateWeightPerSide(exercise.weight, exercise.barWeight)}
                        lb per side
                      </span>
                    </div>
                  </div>}
            </Card>)}
        </div>
      </div>
      <div className="p-4 border-t border-gray-100" data-id="element-156">
        <Button onClick={onNext} variant="primary" fullWidth data-id="element-157">
          See Workout Plan
        </Button>
      </div>
    </div>;
};